import { TextField } from '@mui/material'
import type { ChangeEvent } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { BidDirection } from '@/features/bidding/types/bidLadderInput'

type BidLadderPriceInputProps = {
  value: number | null
  /** Direction for styling. When omitted, uses neutral/symmetrical styling. */
  direction?: BidDirection
  onChange: (value: number | null) => void
  disabled?: boolean
  /** Whether this input has a validation error */
  error?: boolean
}

function BidLadderPriceInput({
  value,
  direction,
  onChange,
  disabled = false,
  error = false,
}: Readonly<BidLadderPriceInputProps>) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState<string>(value?.toString() ?? '')

  // Sync inputValue with value prop changes (e.g., from parent state updates)
  useEffect(() => {
    if (value !== null) {
      setInputValue(value.toString())
    } else {
      setInputValue('')
    }
  }, [value])

  // When direction is omitted (symmetrical), use neutral styling
  const isSymmetrical = direction === undefined

  const getColor = () => {
    if (isSymmetrical) return 'primary.main'
    return direction === 'up' ? 'success.main' : 'error.main'
  }
  const color = getColor()

  const getLabel = () => {
    if (isSymmetrical) return 'Price'
    return direction === 'up'
      ? t('bidding.bidding_cascading.bid_ladder.up_price')
      : t('bidding.bidding_cascading.bid_ladder.down_price')
  }
  const label = getLabel()

  const getTextAlign = () => {
    if (isSymmetrical) return 'center'
    return direction === 'down' ? 'right' : 'left'
  }
  const textAlign = getTextAlign()

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      setInputValue(newValue)

      if (newValue === '') {
        onChange(null)
        return
      }

      const parsed = Number.parseFloat(newValue)
      if (!Number.isNaN(parsed) && parsed >= 0) {
        onChange(parsed)
      }
    },
    [onChange],
  )

  const handleBlur = useCallback(() => {
    // On blur, format the value properly
    if (value !== null) {
      setInputValue(value.toFixed(2))
    }
  }, [value])

  const focusBorderColor = error ? 'error.main' : color

  return (
    <TextField
      disabled={disabled}
      error={error}
      inputProps={{
        step: 0.5,
        min: 0,
        'aria-label': label,
      }}
      size="small"
      sx={{
        width: { xs: 90, sm: 80 },
        '& .MuiInputBase-input': {
          // Use 16px on mobile to prevent iOS zoom on focus
          fontSize: { xs: '1rem', sm: '0.8125rem' },
          padding: '6px 8px',
          textAlign,
          color: error ? 'error.main' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
          // Hide number input spinners
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          MozAppearance: 'textfield',
        },
        '& .MuiOutlinedInput-root': {
          height: { xs: 32, sm: 28 },
          transition: 'box-shadow 0.12s ease',
          '& fieldset': {
            borderColor: 'divider',
            borderWidth: '1px',
          },
          '&:hover fieldset': {
            borderColor: 'action.active',
          },
          '&.Mui-focused fieldset': {
            border: '2px solid',
            borderColor: focusBorderColor,
          },
          '&.Mui-error fieldset': {
            border: '2px solid',
            borderColor: 'error.main',
          },
        },
      }}
      type="number"
      value={inputValue}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  )
}

export default BidLadderPriceInput
